import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, Profile, isSupabaseConfigured } from '../lib/supabase';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string, role: 'admin' | 'crew' | 'client') => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const loadedProfileIdRef = useRef<string | null>(null);

  if (!isSupabaseConfigured) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 max-w-lg">
          <h2 className="text-xl font-semibold text-red-400 mb-2">Configuration Error</h2>
          <p className="text-zinc-300 mb-4">
            Missing Supabase environment variables. Please ensure the following are set:
          </p>
          <ul className="list-disc list-inside text-zinc-400 space-y-1 mb-4">
            <li>VITE_SUPABASE_URL</li>
            <li>VITE_SUPABASE_ANON_KEY</li>
          </ul>
          <p className="text-sm text-zinc-500">
            Check your deployment environment variables in Vercel or your .env file for local development.
          </p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    let mounted = true;
    let initialLoadDone = false;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      initialLoadDone = true;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      // Skip INITIAL_SESSION event since getSession() already handled it
      if (event === 'INITIAL_SESSION') {
        return;
      }

      // Only reload profile on specific auth events
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        (async () => {
          setSession(session);
          setUser(session?.user ?? null);
          if (session?.user) {
            await loadProfile(session.user.id);
          }
        })();
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        loadedProfileIdRef.current = null;
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const loadProfile = async (userId: string) => {
    // Prevent duplicate loads for the same user
    if (loadedProfileIdRef.current === userId) {
      console.log('[AuthContext] Profile already loaded for user:', userId);
      setLoading(false);
      return;
    }

    console.log('[AuthContext] Loading profile for user:', userId);
    loadedProfileIdRef.current = userId;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      console.log('[AuthContext] Profile query result:', { data, error });
      if (error) throw error;
      setProfile(data);
      console.log('[AuthContext] Profile set:', data);
    } catch (error) {
      console.error('[AuthContext] Error loading profile:', error);
      loadedProfileIdRef.current = null;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        return {
          error: {
            message: 'Invalid email or password. Please check your credentials and try again.'
          }
        };
      }
      if (error.message.includes('Email not confirmed')) {
        return {
          error: {
            message: 'Please check your email and confirm your account before signing in.'
          }
        };
      }
    }

    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string, role: 'admin' | 'crew' | 'client') => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: {
          full_name: fullName,
          role: role,
        }
      }
    });

    if (error) {
      if (error.message.includes('User already registered')) {
        return {
          error: {
            message: 'An account with this email already exists. Please sign in instead or reset your password if you forgot it.'
          }
        };
      }
      return { error };
    }

    if (!data.user) {
      return { error: { message: 'Failed to create user account' } };
    }

    await supabase
      .from('crew_members')
      .update({ user_id: data.user.id })
      .eq('email', email)
      .is('user_id', null);

    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const refreshProfile = async () => {
    if (user) {
      loadedProfileIdRef.current = null;
      await loadProfile(user.id);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signIn, signUp, signOut, resetPassword, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
